import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  answerPortalClientRequest,
  downloadPortalDocument,
  getPortalClientRequests,
  getPortalDocuments,
  getPortalProcessDetail,
  uploadPortalDocument,
} from '@services/portalService'
import { formatReportCurrency, formatReportDate } from '@components/shared/reports/ReportPrimitives'
import './portalWorkspace.css'

function formatBytes(value) {
  const bytes = Number(value || 0)
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function formatDateTime(value) {
  if (!value) return '—'
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
}

function saveBlob(blob, fileName) {
  const url = window.URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = fileName || 'documento'
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  window.URL.revokeObjectURL(url)
}

async function downloadDocument(document) {
  const result = await downloadPortalDocument(document.id)
  if (result.success && result.data) saveBlob(result.data, document.nome)
}

export function PortalDocumentList({ documents = [], compact = false }) {
  if (!documents.length) {
    return <div className="portal-workspace-empty compact"><i className="far fa-folder-open" /><span>Nenhum documento disponível.</span></div>
  }

  return (
    <div className={`portal-document-list ${compact ? 'compact' : ''}`}>
      {documents.map((document) => (
        <button key={document.id} type="button" className="portal-document-row" onClick={() => downloadDocument(document)}>
          <div className="portal-document-icon"><i className="far fa-file-lines" /></div>
          <div className="portal-document-copy">
            <strong>{document.nome}</strong>
            <span>
              {document.numeroProcesso ? `Processo ${document.numeroProcesso} · ` : ''}
              {document.origem === 'OFFICE' ? 'Compartilhado pela Veiga' : 'Enviado por você'} · {formatBytes(document.tamanhoBytes)}
            </span>
            {document.descricao ? <small>{document.descricao}</small> : null}
          </div>
          <div className="portal-document-meta">
            <span>{formatDateTime(document.createdAt)}</span>
            <i className="fas fa-arrow-down" />
          </div>
        </button>
      ))}
    </div>
  )
}

function UploadBox({ processoId = null, requestId = null, onUploaded, label = 'Enviar documento' }) {
  const [file, setFile] = useState(null)
  const [description, setDescription] = useState('')
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState('')

  const upload = async () => {
    if (!file || uploading) return
    setError('')
    setUploading(true)
    const result = await uploadPortalDocument(file, { processoId, requestId, descricao: description })
    setUploading(false)
    if (!result.success) {
      setError(result.error || 'Não foi possível enviar o documento.')
      return
    }
    setFile(null)
    setDescription('')
    onUploaded?.(result.data)
  }

  return (
    <div className="portal-upload-box">
      <label className="portal-upload-file">
        <input
          type="file"
          accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.txt,.csv"
          onChange={(event) => setFile(event.target.files?.[0] || null)}
        />
        <i className="fas fa-paperclip" />
        <span>{file ? file.name : label}</span>
      </label>
      {file ? (
        <>
          <input className="portal-upload-description" value={description} onChange={(event) => setDescription(event.target.value)} maxLength={500} placeholder="Descrição opcional" />
          <button type="button" className="portal-primary-button compact" onClick={upload} disabled={uploading}>
            {uploading ? <><i className="fas fa-spinner fa-spin" /> Enviando...</> : 'Enviar arquivo'}
          </button>
        </>
      ) : null}
      <small>PDF, DOCX, XLSX, PNG, JPG, TXT ou CSV · máximo 10 MB</small>
      {error ? <div className="portal-form-error">{error}</div> : null}
    </div>
  )
}

export function PortalProcessDetailDrawer({ processoId, onClose }) {
  const [detail, setDetail] = useState(null)
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const loadSequence = useRef(0)

  const load = useCallback(async () => {
    if (!processoId) return
    const sequence = ++loadSequence.current
    setLoading(true)
    setError('')
    setDetail(null)
    setDocuments([])
    const [detailResult, docsResult] = await Promise.all([
      getPortalProcessDetail(processoId),
      getPortalDocuments({ processoId }),
    ])
    if (sequence !== loadSequence.current) return
    if (detailResult.success) setDetail(detailResult.data)
    else setError(detailResult.error || 'Não foi possível carregar o processo.')
    if (docsResult.success) setDocuments(Array.isArray(docsResult.data) ? docsResult.data : [])
    setLoading(false)
  }, [processoId])

  useEffect(() => {
    load()
    return () => { loadSequence.current += 1 }
  }, [load])

  if (!processoId) return null

  return (
    <div className="portal-process-drawer-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose() }}>
      <aside className="portal-process-drawer" aria-label="Detalhes do processo">
        <header className="portal-process-drawer-header">
          <div>
            <span className="portal-eyebrow">Detalhes do processo</span>
            <h2>{detail?.numeroProcesso || `Processo #${processoId}`}</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Fechar"><i className="fas fa-xmark" /></button>
        </header>

        {loading ? <div className="portal-workspace-loading"><i className="fas fa-spinner fa-spin" /> Carregando detalhes...</div> : error ? (
          <div className="portal-workspace-error"><i className="fas fa-circle-exclamation" /> {error}</div>
        ) : detail ? (
          <div className="portal-process-drawer-body">
            <section className="portal-friendly-summary">
              <span>Em poucas palavras</span>
              <h3>{detail.resumoExecutivo}</h3>
              <p>{detail.statusExplicado}</p>
            </section>

            <section className="portal-detail-card portal-latest-update">
              <div className="portal-detail-card-title"><i className="far fa-clock" /><span>Última atualização</span></div>
              <p>{detail.atualizacaoExplicada}</p>
              <small>{detail.mensagemAcompanhamento}</small>
            </section>

            <section className="portal-detail-card">
              <div className="portal-detail-card-title"><i className="fas fa-scale-balanced" /><span>Informações do processo</span></div>
              <dl className="portal-process-detail-grid">
                <div><dt>Área</dt><dd>{detail.area || '—'}</dd></div>
                <div><dt>Fase</dt><dd>{detail.fase || '—'}</dd></div>
                <div><dt>Ação</dt><dd>{detail.acao || '—'}</dd></div>
                <div><dt>Rito</dt><dd>{detail.rito || '—'}</dd></div>
                <div><dt>Situação</dt><dd>{detail.situacao || detail.status || '—'}</dd></div>
                <div><dt>Perspectiva de perda</dt><dd>{detail.perspectivaPerda || '—'}</dd></div>
                <div className="wide"><dt>Partes</dt><dd>{detail.partePrincipal || '—'} <span>×</span> {detail.parteContraria || '—'}</dd></div>
                <div className="wide"><dt>Local</dt><dd>{[detail.vara, detail.comarca, detail.estado].filter(Boolean).join(' · ') || detail.foro || '—'}</dd></div>
                <div className="wide"><dt>Objeto</dt><dd>{detail.objeto || 'Não informado'}</dd></div>
                <div><dt>Profissão / cargo do reclamante</dt><dd>{detail.cargoEmpregado || 'Não informado'}</dd></div>
                {detail.detalhamentoObjetoPedidos ? <div className="wide"><dt>Detalhamento do objeto / pedidos</dt><dd>{detail.detalhamentoObjetoPedidos}</dd></div> : null}
              </dl>
            </section>

            <section className="portal-process-dates">
              <div><span>Entrada</span><strong>{formatReportDate(detail.dataEntrada)}</strong></div>
              <div><span>Distribuição</span><strong>{formatReportDate(detail.dataDistribuicao)}</strong></div>
              <div><span>Encerramento</span><strong>{formatReportDate(detail.dataEncerramento)}</strong></div>
            </section>

            <section className="portal-detail-card">
              <div className="portal-detail-card-title"><i className="fas fa-chart-line" /><span>Provisões registradas</span></div>
              <div className="portal-provision-grid">
                <div><span>Valor inicial da causa</span><strong>{formatReportCurrency(detail.valorInicialCausa)}</strong></div>
                <div><span>Possível</span><strong>{formatReportCurrency(detail.valorProvisaoPossivel)}</strong></div>
                <div><span>Remota</span><strong>{formatReportCurrency(detail.valorProvisaoRemota)}</strong></div>
                <div><span>Provável</span><strong>{formatReportCurrency(detail.valorProvisaoProvavel)}</strong></div>
              </div>
            </section>

            <section className="portal-detail-card">
              <div className="portal-detail-card-title"><i className="far fa-folder-open" /><span>Documentos deste processo</span></div>
              <PortalDocumentList documents={documents} compact />
              <UploadBox processoId={processoId} onUploaded={() => load()} label="Enviar documento para este processo" />
            </section>
          </div>
        ) : null}
      </aside>
    </div>
  )
}

const REQUEST_STATUS = {
  OPEN: { label: 'Aguardando você', className: 'open' },
  ANSWERED: { label: 'Respondida', className: 'answered' },
  CLOSED: { label: 'Concluída', className: 'closed' },
}

function ClientRequestCard({ request, onChanged }) {
  const [response, setResponse] = useState(request.respostaCliente || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const status = REQUEST_STATUS[request.status] || { label: request.status, className: '' }
  const canInteract = request.status !== 'CLOSED'

  const answer = async () => {
    setSaving(true)
    setError('')
    const result = await answerPortalClientRequest(request.id, response)
    setSaving(false)
    if (!result.success) {
      setError(result.error || 'Não foi possível enviar a resposta.')
      return
    }
    onChanged?.()
  }

  return (
    <article className="portal-client-request-card">
      <header>
        <div>
          <span className="portal-request-type"><i className={`fas ${request.tipo === 'DOCUMENT' ? 'fa-file-arrow-up' : 'fa-circle-question'}`} /> {request.tipo === 'DOCUMENT' ? 'Documento solicitado' : 'Informação solicitada'}</span>
          <h3>{request.titulo}</h3>
        </div>
        <span className={`portal-client-request-status ${status.className}`}>{status.label}</span>
      </header>
      <p>{request.descricao}</p>
      <div className="portal-request-context">
        {request.numeroProcesso ? <span><i className="fas fa-scale-balanced" /> Processo {request.numeroProcesso}</span> : null}
        {request.prazoResposta ? <span><i className="far fa-calendar" /> Responder até {formatReportDate(request.prazoResposta)}</span> : null}
        <span><i className="far fa-clock" /> Solicitado em {formatDateTime(request.createdAt)}</span>
      </div>

      {request.documentos?.length ? <PortalDocumentList documents={request.documentos} compact /> : null}

      {canInteract ? (
        <div className="portal-client-request-response">
          {request.tipo === 'DOCUMENT' ? <UploadBox requestId={request.id} processoId={request.processoId} onUploaded={onChanged} label="Selecionar documento solicitado" /> : null}
          <label>
            <span>{request.tipo === 'DOCUMENT' ? 'Mensagem para a equipe Veiga (opcional)' : 'Sua resposta'}</span>
            <textarea rows={3} value={response} onChange={(event) => setResponse(event.target.value)} maxLength={4000} placeholder="Escreva sua resposta aqui..." />
          </label>
          {error ? <div className="portal-form-error">{error}</div> : null}
          {(response.trim() || request.tipo === 'INFORMATION') ? (
            <button type="button" className="portal-primary-button compact" onClick={answer} disabled={saving || (request.tipo === 'INFORMATION' && !response.trim())}>
              {saving ? <><i className="fas fa-spinner fa-spin" /> Enviando...</> : 'Enviar resposta'}
            </button>
          ) : null}
        </div>
      ) : request.respostaCliente ? <div className="portal-request-final-answer"><span>Sua resposta</span><p>{request.respostaCliente}</p></div> : null}
    </article>
  )
}

export function PortalClientRequestsPanel({ onOpenCountChange }) {
  const [requests, setRequests] = useState([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('')
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    const result = await getPortalClientRequests(filter)
    if (result.success) {
      const items = Array.isArray(result.data) ? result.data : []
      setRequests(items)
      if (!filter) onOpenCountChange?.(items.filter((item) => item.status === 'OPEN').length)
    } else {
      setError(result.error || 'Não foi possível carregar suas pendências.')
    }
    setLoading(false)
  }, [filter, onOpenCountChange])

  useEffect(() => { load() }, [load])

  return (
    <section className="portal-workspace-section portal-requests-workspace">
      <header className="portal-workspace-heading">
        <div><span className="portal-eyebrow">Pendências</span><h1>O que precisamos de você</h1><p>Envie documentos ou informações solicitadas pela equipe Veiga diretamente por aqui.</p></div>
        <select value={filter} onChange={(event) => setFilter(event.target.value)}>
          <option value="">Todas</option><option value="OPEN">Aguardando resposta</option><option value="ANSWERED">Respondidas</option><option value="CLOSED">Concluídas</option>
        </select>
      </header>
      {loading ? <div className="portal-workspace-loading"><i className="fas fa-spinner fa-spin" /> Carregando pendências...</div> : error ? <div className="portal-workspace-error">{error}</div> : requests.length ? (
        <div className="portal-client-request-list">{requests.map((request) => <ClientRequestCard key={request.id} request={request} onChanged={load} />)}</div>
      ) : <div className="portal-workspace-empty"><i className="far fa-circle-check" /><h3>Nenhuma pendência por aqui.</h3><p>Quando a equipe Veiga precisar de alguma informação ou documento, a solicitação aparecerá nesta área.</p></div>}
    </section>
  )
}

export function PortalDocumentsPanel() {
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const load = async () => {
    setLoading(true)
    const result = await getPortalDocuments()
    if (result.success) setDocuments(Array.isArray(result.data) ? result.data : [])
    else setError(result.error || 'Não foi possível carregar os documentos.')
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const officeDocuments = useMemo(() => documents.filter((item) => item.origem === 'OFFICE'), [documents])
  const clientDocuments = useMemo(() => documents.filter((item) => item.origem === 'CLIENT'), [documents])

  return (
    <section className="portal-workspace-section portal-documents-workspace">
      <header className="portal-workspace-heading"><div><span className="portal-eyebrow">Central de documentos</span><h1>Arquivos compartilhados</h1><p>Consulte documentos enviados pela Veiga e o histórico dos arquivos que você já encaminhou.</p></div></header>
      {loading ? <div className="portal-workspace-loading"><i className="fas fa-spinner fa-spin" /> Carregando documentos...</div> : error ? <div className="portal-workspace-error">{error}</div> : (
        <div className="portal-document-groups">
          <div className="portal-document-group"><div className="portal-document-group-title"><span>Compartilhados pela Veiga</span><strong>{officeDocuments.length}</strong></div><PortalDocumentList documents={officeDocuments} /></div>
          <div className="portal-document-group"><div className="portal-document-group-title"><span>Enviados por você</span><strong>{clientDocuments.length}</strong></div><PortalDocumentList documents={clientDocuments} /></div>
        </div>
      )}
    </section>
  )
}
